/**
 * The waiting animation: **the street lights up.**
 *
 * The mark is a street of three buildings — a short block, a house with a keyhole, a taller
 * block. This is that street, unchanged, with a light travelling along it: the left block wakes,
 * then the house, then the tall block, and round again. Nothing spins, nothing leaves the ground
 * line and no shape is ever absent, so at every moment of the loop the thing on screen is still
 * the logo. The house's keyhole is drawn only while the house is lit, which puts the handover at
 * the centre of every cycle.
 *
 * **The geometry and the rules are not in this file.** The paths come from `LOADER_BUILDINGS` in
 * `packages/core/src/brand.ts`, which the listing site and the exported `assets/brand/loader/`
 * SVG read as well, so the three cannot drift into three different animations. The CSS is
 * `LOADER_CSS` in the same file, restated in `apps/web/src/index.css` the way the wordmark's rules
 * are — Tailwind's stylesheet cannot import a TypeScript string. If you change one, change both;
 * `node tools/verify-loader.mjs` fails when they disagree.
 *
 * **Three sizes, one drawing.** `sm` is 20px and belongs on a button's own line; `md` is 48px for
 * a panel that is fetching; `lg` is 96px for a screen that has nothing on it yet. The windows stop
 * being visible below about 40px, which is intended — what carries the small size is the
 * brightness moving across three big silhouettes.
 *
 * **It reports, it is not decoration.** The wrapper is a `role="status"` with a visually hidden
 * label inside it, so a screen reader is told the wait has begun without the drawing being
 * announced as a second thing. Give `label` a sentence that says what is being waited for —
 * "Loading your invoices" beats "Loading" — and never pass an empty string.
 *
 * ```tsx
 * <BrandSpinner size="sm" label="Saving the unit" />
 * ```
 *
 * Anyone waiting on `prefers-reduced-motion` gets the finished mark with a four-second breath
 * instead of the travelling light; that is handled entirely in the stylesheet.
 */

import { LOADER_BUILDINGS, LOADER_LABEL } from "@rentorng/core";

export type BrandSpinnerSize = "sm" | "md" | "lg";

/**
 * The colour a building is painted, so the **module skin can move it**.
 *
 * `LOADER_BUILDINGS` carries the brand's own greens as literal hex, which is right for
 * `packages/core` — the listing site, the exported `assets/brand/loader/` and anything else that
 * renders the mark outside this application have no module and must stay green. Baked into the
 * `fill` attribute here, though, they were the one part of the shell the development skin could not
 * reach: everything around them turned blue and the spinner stayed green, which reads as a stuck
 * asset rather than as a decision.
 *
 * So each building's fill becomes a custom property **with its own hex as the fallback**. Nothing
 * changes for letting, or for a page that never sets the variable — including a screenshot test that
 * renders this component on its own. `[data-module="developing"]` in `index.css` sets the three, and
 * `tools/verify-loader.mjs` still compares the geometry, which is what it was written to protect.
 *
 * The index rather than the colour keys the variable: two of the three buildings share a fill, and
 * naming the variable after the colour would have meant the skin could not tell them apart.
 */
function paint(fill: string, index: number): string {
  return `var(--ldr-fill-${index + 1}, ${fill})`;
}

export function BrandSpinner({
  size = "md",
  label = LOADER_LABEL,
  className = "",
}: {
  /** 20px beside a control, 48px in a panel, 96px on an empty screen. */
  size?: BrandSpinnerSize;
  /** What a screen reader says. Name the thing being waited for where you can. */
  label?: string;
  /** Layout only — margins and alignment. The size belongs to `size`. */
  className?: string;
}) {
  return (
    <span className={`rentorng-loader rentorng-loader-${size} ${className}`.trim()} role="status">
      <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        {LOADER_BUILDINGS.map((building, index) => (
          <g key={index} className={`rentorng-loader-b rentorng-loader-b${index + 1}`}>
            <path
              fill={paint(building.fill, index)}
              fillRule="evenodd"
              {...(building.stroke
                ? { stroke: building.stroke, strokeWidth: 10, strokeLinejoin: "round" as const }
                : {})}
              d={building.d}
            />
            {/*
              The cover over a window or the keyhole: the same shape as the knockout beneath it,
              painted in the building's own colour so the building rests as a plain silhouette and
              its windows appear only when the light comes on. Stroked as well as filled, because
              the house's keyhole carries a hairline in the neighbours' green and half of a stroke
              stands outside the shape it is drawn on.
            */}
            {building.lights.map((d, light) => (
              <path
                key={light}
                className="rentorng-loader-shut"
                fill={paint(building.fill, index)}
                stroke={paint(building.fill, index)}
                strokeWidth={10}
                strokeLinejoin="round"
                d={d}
              />
            ))}
          </g>
        ))}
      </svg>
      <span className="rentorng-loader-label">{label}</span>
    </span>
  );
}
